// Global tracker that records the user's last interaction target outside
// the bug-report panel. Read by `bugReportPayload.ts` to enrich the
// `state_snapshot` and `devSyncMetadata` of every submission with a
// concrete "what were they clicking on?" snapshot.
//
// Design notes:
// - Listens with `capture: true` so we see events before stopPropagation.
// - Skips events whose target is inside `[data-bug-report-panel]` so the
//   act of opening the panel doesn't overwrite the snapshot.
// - Uses a WeakRef to avoid pinning detached DOM nodes (modals close,
//   leads unmount, etc.). If the element is GC'd we return null.
// - Lazily initialized via `installInteractionTracker()` at app boot in
//   `src/main.tsx`. Calling it twice is safe (idempotent).
// - The summarizer is exported so tests can hit it without DOM setup.
//
// See docs/planning/bug_report_active_context.md.

export type InteractionTargetSnapshot = {
  tagName: string | null;
  id: string | null;
  testid: string | null;
  role: string | null;
  label_preview: string | null;
  panel_chain: string[];
  age_ms: number | null;
};

type Stored = {
  ref: WeakRef<Element>;
  capturedAtMs: number;
};

let _last: Stored | null = null;
let _installed = false;

const LABEL_PREVIEW_MAX = 80;

function shouldIgnoreTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return true;
  // Bug-report panel and its modal backdrop must not overwrite the snapshot.
  if (target.closest("[data-bug-report-panel]")) return true;
  // Elevation modal also opens FROM dev-sync triage; ignore its events too
  // so the "what was the user looking at before reporting" stays accurate.
  if (target.closest("[data-elevate-bug-modal]")) return true;
  return false;
}

function handleInteraction(event: Event) {
  const target = event.target;
  if (shouldIgnoreTarget(target)) return;
  _last = { ref: new WeakRef(target as Element), capturedAtMs: Date.now() };
}

/**
 * Idempotent. Safe to call multiple times. Best invoked once at app
 * bootstrap (see `src/main.tsx`).
 */
export function installInteractionTracker(): void {
  if (_installed || typeof document === "undefined") return;
  _installed = true;
  const opts: AddEventListenerOptions = { capture: true };
  const passiveOpts: AddEventListenerOptions = { capture: true, passive: true };
  document.addEventListener("mousedown", handleInteraction, opts);
  document.addEventListener("focusin", handleInteraction, opts);
  document.addEventListener("touchstart", handleInteraction, passiveOpts);
}

/** Test-only — resets the singleton state. Not exported from the barrel. */
export function __resetInteractionTrackerForTests(): void {
  _last = null;
  _installed = false;
}

export function readLastInteractionSnapshot(): InteractionTargetSnapshot | null {
  if (!_last) return null;
  const target = _last.ref.deref();
  if (!target || !target.isConnected) return null;
  const snap = summarizeElement(target);
  return { ...snap, age_ms: Math.max(0, Date.now() - _last.capturedAtMs) };
}

/**
 * Minimal Element interface the summarizer needs. Keeping this small lets
 * us unit-test the summarizer from node (no jsdom dep) while staying
 * structurally compatible with the real DOM Element / HTMLElement.
 */
export type ElementLike = {
  tagName?: string | null;
  id?: string | null;
  getAttribute(name: string): string | null;
  parentElement?: ElementLike | null;
  innerText?: string | null;
  textContent?: string | null;
};

/**
 * Pure summarizer. Exported so tests can hit it directly without DOM.
 * `age_ms` is always `null` from this entry point — it's set in
 * `readLastInteractionSnapshot` where the timestamp is known.
 */
export function summarizeElement(el: ElementLike): InteractionTargetSnapshot {
  const panelChain = collectPanelChain(el);
  const testid = el.getAttribute("data-testid");
  const role = el.getAttribute("role");
  const label = readLabelPreview(el);
  return {
    tagName: el.tagName ? el.tagName.toLowerCase() : null,
    id: el.id ? el.id : null,
    testid: testid && testid.trim() ? testid.trim() : null,
    role: role && role.trim() ? role.trim() : null,
    label_preview: label,
    panel_chain: panelChain,
    age_ms: null,
  };
}

function collectPanelChain(start: ElementLike): string[] {
  // Walk ancestors collecting `data-app-panel` values. Outer panel ends up
  // at index 0; inner-most (closest to the focused element) at the tail.
  // This matches how triagers will visually scan the chain.
  const chain: string[] = [];
  let cur: ElementLike | null = start;
  while (cur) {
    const name = cur.getAttribute("data-app-panel");
    if (name && name.trim()) chain.unshift(name.trim());
    cur = cur.parentElement ?? null;
  }
  return chain;
}

function readLabelPreview(el: ElementLike): string | null {
  // Prefer the most-specific accessible label, then fall back to text.
  // We do NOT include `value` from inputs — that field could leak PII
  // (phone numbers, etc.).
  const aria = el.getAttribute("aria-label");
  if (aria && aria.trim()) return aria.trim().slice(0, LABEL_PREVIEW_MAX);
  const text = ((el.innerText || el.textContent) ?? "").trim();
  if (text) return text.slice(0, LABEL_PREVIEW_MAX);
  return null;
}
